import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {SettingService} from "../../services/setting.service";
import {Setting} from "../../models/setting";
import {SettingEditComponent} from "./setting-edit/setting-edit.component";
import {MatDialog} from "@angular/material/dialog";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {NGXLogger} from "ngx-logger";
import {AuthService} from "../../../core/auth/auth.service";
import {Permissions} from "../../../core/auth/permissions";
import { take } from 'rxjs';
import { DeleteDialogComponent } from '../delete-dialog/delete-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { LoadingDialogService } from '../../services/loading-dialog.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsComponent implements OnInit {
  @Input() brandId!: number;
  @Input() productUuid!: string;
  @Input() title?: string;
  settings! : Array<Setting>;
  displayedColumns: string[] = ['name', 'description', 'value', 'language', 'version', 'edit'];
  versions: number[] = [1];
  selectedVersion : number = 1;
  dataSource = new MatTableDataSource<Setting>(this.settings);
  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  allowGlobalSettingCreate : boolean = false;
  allowGlobalSettingEdit : boolean = false;
  allowGlobalSettingDelete : boolean = false;

  /**
   * Constructor
   * @param settingService Settings service instance.
   * @param dialog Dialog reference.
   * @param ref Change detector reference.
   * @param logger Logger instance.
   */
  constructor(private settingService : SettingService,
              public dialog: MatDialog,
              private ref: ChangeDetectorRef,
              private logger: NGXLogger,
              private authService : AuthService,
              private toastr: ToastrService,
              private loadingDialogService: LoadingDialogService) {
    ref.detach();
    setInterval(() => {
      this.ref.detectChanges();
    }, 200);
  }

  /**
   * Init
   */
  ngOnInit(): void {
    this.settingService.getSettingVersions(this.brandId).then((versions) => {
        this.versions = versions;
        // Set version dropdown to highest available version.
        this.selectedVersion = this.versions[this.versions.length -1 ];
        this.loadVersion();
      }
    );

    this.authService.role.pipe(take(1)).subscribe(role => {
      let accessPermissionList : string[] = this.authService.getAccessPermissions(role);

      if(accessPermissionList.includes(Permissions.Type.ADD_GLOBAL_SETTING)) { this.allowGlobalSettingCreate = true; }
      if(accessPermissionList.includes(Permissions.Type.EDIT_GLOBAL_SETTING)) { this.allowGlobalSettingEdit = true; }
      if(accessPermissionList.includes(Permissions.Type.DELETE_GLOBAL_SETTING)) { this.allowGlobalSettingDelete = true; }
    });
  }

  /**
   * Load version settings.
   */
  loadVersion () {
    if (this.brandId == undefined) {
      this.settingService.getGlobalSettings(this.selectedVersion).then((settings) => {
        this.ref.markForCheck();
        this.settings = settings;
          this.dataSource = new MatTableDataSource<Setting>(this.settings);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        }
      );
    }
    else {
      this.settingService.getBrandSettings(this.selectedVersion, this.brandId, this.productUuid).then((settings) => {
          this.settings = settings;
          this.dataSource = new MatTableDataSource<Setting>(this.settings);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.ref.markForCheck();
        }
      );
    }
  }

  /**
   * When user selects a different version.
   */
  onVersionChange() {
    this.loadVersion();
  }

  /**
   * Edit settings
   * @param setting Setting to edit.
   */
  edit(setting : Setting): void {
    this.logger.debug("Editing Product:" + JSON.stringify(setting));
    let dialogRef = this.dialog.open(SettingEditComponent, {
      data: { setting: setting,  title : "Edit" },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != "" && result != undefined) {
        this.logger.debug(`Dialog result: ${JSON.stringify(result)}`);
        if (this.brandId == undefined) {
          //Global setting.
          this.settingService.updateGlobalSetting(result).then (result => {
            this.loadVersion();
          },
          error => {
            this.logger.debug(error);
          }
          )
        }
        else {
          //Brand setting.
          this.settingService.updateBrandSetting(result, this.brandId, this.productUuid).then (result => {
              this.loadVersion();
            },
            error => {
              this.logger.debug(error);
            }
          )
        }
      };
    });
  }

  /**
   * Add new setting
   */
  add() {

    let setting : Setting = new Setting(0, "", "", "",
      "", "EN", 1);

    let dialogRef = this.dialog.open(SettingEditComponent, {
      data: { setting: setting,  title : "Edit" },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != "" && result != undefined) {
        this.loadingDialogService.openDialog();
        this.logger.debug(`Dialog result: ${JSON.stringify(result)}`);
        if (this.brandId == undefined) {
          //Global setting.
          this.settingService.updateGlobalSetting(result).then (result => {
              this.loadVersion();
              this.loadingDialogService.hideDialog();
            },
            error => {
              this.logger.debug(error);
              this.loadingDialogService.hideDialog();
            }
          )
        }
        else {
          //Brand setting.
          this.settingService.updateBrandSetting(result, this.brandId, this.productUuid).then (result => {
              this.loadVersion();
              this.loadingDialogService.hideDialog();
            },
            error => {
              this.logger.debug(error);
              this.loadingDialogService.hideDialog();
            }
          )
        }
      };
      
    });
  }

  /**
   * Delete setting
   * @param setting Setting to delete.
   */
  delete(setting : Setting) : void {
    let dialogRef = this.dialog.open(DeleteDialogComponent, {
      data: { setting: setting,  title : "Confirm Delete" },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result != "" && result != undefined) {
        this.loadingDialogService.openDialog();
        this.settingService.deleteSetting(setting.id as number).then((settings) => {
        this.toastr.success(`Application setting ${setting.name} deleted successfully.`, 'Setting Deleted');
        this.loadVersion();
        this.loadingDialogService.hideDialog();
        });
      }

    });

  }
}
